/**
 * Origin-aware host detection.
 *
 * Determines whether the app should talk to the `enex.sk` or `enexis.cz`
 * data API, and whether the UI should default to Czech. Resolved once at
 * module load and cached in sessionStorage so client-side navigation
 * (which clears document.referrer) doesn't flip the host back.
 */

export type ApiHost = 'enex.sk' | 'enexis.cz';

export interface OriginContext {
  apiHost: ApiHost;
  isCz: boolean;
}

const STORAGE_KEY = 'origin-host';

function hostnameOf(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return '';
  }
}

function resolve(): ApiHost {
  if (typeof window === 'undefined') return 'enex.sk';

  // 1) `?host=` override — highest priority; overwrites any cache.
  try {
    const params = new URLSearchParams(window.location.search);
    const override = params.get('host');
    if (override === 'enex.sk' || override === 'enexis.cz') {
      try { window.sessionStorage.setItem(STORAGE_KEY, override); } catch { /* ignore */ }
      return override;
    }
  } catch { /* ignore */ }

  // 2) Cached
  try {
    const cached = window.sessionStorage.getItem(STORAGE_KEY);
    if (cached === 'enex.sk' || cached === 'enexis.cz') return cached;
  } catch { /* sessionStorage may be unavailable */ }

  let host: ApiHost = 'enex.sk';

  // 3) Current hostname
  if (window.location.hostname.endsWith('.cz')) {
    host = 'enexis.cz';
  } else if (document.referrer && hostnameOf(document.referrer).endsWith('.cz')) {
    // 4) Referrer
    host = 'enexis.cz';
  }

  try { window.sessionStorage.setItem(STORAGE_KEY, host); } catch { /* ignore */ }
  return host;
}

let cached: OriginContext | null = null;

export function getOriginContext(): OriginContext {
  if (cached) return cached;
  const apiHost = resolve();
  cached = { apiHost, isCz: apiHost === 'enexis.cz' };
  return cached;
}

/** Base URL for the data API, matching the resolved origin. */
export function getApiBaseUrl(): string {
  return `https://www.${getOriginContext().apiHost}/dataapi`;
}

export type CurrencyCode = 'EUR' | 'CZK';
export interface Currency {
  code: CurrencyCode;
  symbol: '€' | 'Kč';
}

export type DataSource = 'api' | 'upload';

let dataSource: DataSource = 'upload';
const listeners = new Set<(c: Currency) => void>();

/** Language-derived currency for manual uploads (cs → Kč, else €). */
function currencyFromLanguage(): Currency {
  const lang = typeof document !== 'undefined' ? document.documentElement.lang : '';
  return lang === 'cs' ? { code: 'CZK', symbol: 'Kč' } : { code: 'EUR', symbol: '€' };
}

/**
 * Display currency. API data: the server is authoritative (it supplies the
 * prices) — enexis.cz → Kč, enex.sk → €. Manual upload: the UI language.
 * Label only — values are never converted.
 */
export function getCurrency(): Currency {
  if (dataSource === 'api') {
    return getOriginContext().isCz
      ? { code: 'CZK', symbol: 'Kč' }
      : { code: 'EUR', symbol: '€' };
  }
  return currencyFromLanguage();
}

/** Set where the current report's data came from; call before the report mounts. */
export function setCurrencySource(source: DataSource): void {
  dataSource = source;
  const c = getCurrency();
  listeners.forEach(l => l(c));
}

/** Subscribe to currency changes (used to keep the i18n `{{currency}}` variable in sync). */
export function onCurrencyChange(listener: (c: Currency) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
